import { Suspense, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn, LoadingState } from '@platform/ui-kit'
import { api } from '@/lib/api'
import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { SearchPalette } from './SearchPalette'
import { PushEnrollmentGate } from './PushEnrollmentGate'
import { NavProgress } from './NavProgress'
import { ErrorBoundary } from '@/app/errors/ErrorBoundary'
import { ChatFrameHost } from '@/features/chat/ChatFrameHost'
import { findModuleByPath } from '@/app/routes/moduleRegistry'
import { OPEN_NAV_EVENT } from '@/features/chat/shellEvents'

/* ------------------------------------------------------------------
   App Shell — سند مادر بند ۵.۲ و ۱۱.۱
   ------------------------------------------------------------------
   «App Shell مالک Login، Layout، Top Bar، Sidebar، Search، Inbox،
    Error Boundary و Route Guard است.»

   Shell هیچ منطق دامنه‌ای ندارد و هیچ ماژولی را مستقیم نمی‌شناسد؛
   فقط رجیستری ماژول‌ها را می‌خواند.
   ------------------------------------------------------------------ */

interface BadgeCounts {
  inbox: number
  notifications: number
}

export function AppShell() {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  /* شمارنده‌ها از Read Model بهینه خوانده می‌شوند، نه Query زنده سنگین
     روی تمام جداول (بند ۱۰.۱). */
  const { data: badges } = useQuery<BadgeCounts>({
    queryKey: ['shell', 'badges'],
    queryFn: async () => {
      const response = await api.get<{ data: BadgeCounts }>('/me/badges')
      return response.data.data
    },
    refetchInterval: 60_000,
    // خرابی شمارنده نباید Shell را از کار بیندازد (اصل P-06)
    retry: 1,
    initialData: { inbox: 0, notifications: 0 },
  })

  const onChat = location.pathname === '/app/chat' || location.pathname.startsWith('/app/chat/')

  /* 🔴 گوشی: «≡» ِ نوارِ گفتگو منوی پنل را باز می‌کند — توضیح در `shellEvents.ts` */
  useEffect(() => {
    const open = () => setSidebarOpen(true)
    window.addEventListener(OPEN_NAV_EVENT, open)
    return () => window.removeEventListener(OPEN_NAV_EVENT, open)
  }, [])

  /* بستن منوی موبایل با تغییر مسیر */
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  /* قفل اسکرول پشت کشوی موبایل — همان الگوی Dialog و SearchPalette.

     فقط زیر lg قفل می‌شود: در دسکتاپ سایدبار همیشه باز است و قفل کردن
     یعنی صفحه هیچ‌وقت اسکرول نشود. عرض از همان نقطه‌شکست CSS خوانده
     می‌شود (64rem) تا با کلاس‌های lg: از هم جدا نیفتند.

     ⚠️ نحو بازه‌ای اگر پارس نشود matches همیشه false می‌دهد و نتیجه‌اش
     قفل‌شدن اسکرولِ دسکتاپ است — بدترین حالت. پس به نحو قدیمی min-width
     برمی‌گردیم که همه‌جا پشتیبانی می‌شود. */
  useEffect(() => {
    if (!sidebarOpen) return

    const desktop = window.matchMedia('(min-width: 64rem)')
    const { overflow } = document.body.style

    /* با چرخاندن دستگاه یا تغییر اندازه پنجره ممکن است از موبایل به
       دسکتاپ برویم؛ آن‌وقت قفل باید برداشته شود وگرنه صفحه در دسکتاپ
       گیر می‌کند. */
    const sync = () => {
      document.body.style.overflow = desktop.matches ? overflow : 'hidden'
    }

    sync()
    desktop.addEventListener('change', sync)
    return () => {
      desktop.removeEventListener('change', sync)
      document.body.style.overflow = overflow
    }
  }, [sidebarOpen])

  /* میان‌بر جست‌وجو */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  /* نام ماژول جاری — برای پیام Error Boundary */
  const currentModule = findModuleByPath(location.pathname.split('/')[2] ?? '')

  return (
    /* 🔴 اینجا overflow-x-hidden بود و باید برداشته می‌شد.

       هدفش درست بود (در موبایل ستون محتوا به اندازهٔ عرض سایدبار
       جابه‌جا می‌شود و نباید اسکرول افقی بسازد) ولی جای اشتباهی بود:
       این div والدِ مستقیم سایدبار است، و overflow-x: hidden مقدار
       overflow-y را به auto محاسبه می‌کند. پس این جعبه خودش تبدیل به
       scroll container می‌شد و سایدبارِ sticky داخل جعبه‌ای گیر می‌کرد
       که ارتفاعش با محتوا رشد می‌کند — یعنی sticky هیچ‌وقت نمی‌چسبید.

       همان کار حالا روی html انجام می‌شود (به index.css نگاه کنید)،
       جایی که مقدار به viewport منتقل می‌شود و اسکرول‌کننده خودِ
       viewport می‌ماند.

       ⚠️ این کلاس را اینجا برنگردانید؛ باگ سایدبار برمی‌گردد. */
    /* پوسته پس از صفحهٔ آغاز نرم پدیدار می‌شود (‎.pf-fade-enter‎ ِ tokens.css)، نه یک‌باره */
    <div className="pf-fade-enter flex min-h-dvh bg-[var(--pf-bg)]">
      <NavProgress />
      <a href="#pf-main" className="pf-skip-link">
        پرش به محتوای اصلی
      </a>

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        inboxCount={badges?.inbox ?? 0}
      />

      {/* کشوی «هُل‌دهنده»: با باز شدن منو، کل ستون محتوا (نوار بالا +
          صفحه) به سمت چپ می‌رود و سایدبار جای خالی سمت راست را می‌گیرد،
          به‌جای اینکه رویش بیفتد. در دسکتاپ سایدبار همیشه باز است و
          جابه‌جایی معنا ندارد. */}
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          'transition-transform duration-[var(--pf-duration)] ease-[var(--pf-ease)]',
          /* ⚠️ translate در Tailwind منطقی نیست و RTL را نمی‌فهمد؛ همیشه
             محور فیزیکی است. پس برای بردن محتوا به چپ باید مقدار منفی
             داد، حتی در سند راست‌به‌چپ. */
          'lg:translate-x-0',
          sidebarOpen && '-translate-x-[var(--pf-sidebar-w)]',
        )}
      >
        <TopBar
          onToggleSidebar={() => setSidebarOpen((value) => !value)}
          onOpenSearch={() => setSearchOpen(true)}
          notificationCount={badges?.notifications ?? 0}
        />

        {/* محتوا داخل یک ظرف گِرد می‌نشیند.

            ظرف بدون فاصله بیرونی است و مستقیم به نوار بالا و سایدبار
            می‌چسبد؛ فقط گوشه‌اش گِرد است. قاب در هر عرضی هست — موبایل
            هم — تا محتوا همه‌جا روی سطح روشن بنشیند نه مستقیم روی زمینه.

            نوار پایین حذف شده و ناوبری موبایل فقط از راه کشوی همبرگری
            است، پس محتوا تا انتهای صفحه ادامه پیدا می‌کند. */}
        <main id="pf-main" className="flex-1">
          <div
            className={cn(
              'w-full px-4 py-4 sm:px-[var(--pf-content-pad)] sm:py-[var(--pf-content-pad)]',
              /* min-h تا ظرف در صفحات کم‌محتوا هم قاب کامل داشته باشد،
                 نه یک نوار باریک وسط زمینه. */
              /* فقط دو گوشهٔ قطری گِرد می‌مانند: بالا-راست و پایین-چپ.
                 ⚠️ عمداً فیزیکی (tl/br) نوشته شده نه منطقی (ss/ee): این
                 دو گوشه همان‌هایی هستند که روی صفحه دیده می‌شوند و با
                 عوض‌شدن جهت سند نباید جابه‌جا شوند. */
              'min-h-full rounded-[var(--pf-radius-xl)] rounded-tl-none rounded-br-none border border-[var(--pf-border)]',
              'bg-[var(--pf-surface)]',
              'relative isolate overflow-hidden',
              /* 🔴 گفتگو در گوشی تمامِ صفحه است، بی قاب و گوشهٔ گِرد — مثلِ واتساپ */
              onChat && 'max-md:rounded-none max-md:border-0',
            )}
          >
            {/*
              🔴 نشانِ کم‌رنگِ گوشهٔ ماژول برداشته شد.

              آن تصویر با `z-10` **روی** محتوا می‌نشست — عمدی بود، چون
              کارت‌ها مات‌اند و پشتِ آن‌ها دیده نمی‌شد. ولی نتیجه‌اش این
              بود که هر کارت و هر جدولی در سامانه یک لکهٔ محوِ ۷٪ روی
              خودش داشت؛ در صفحه‌های پرکارت مثل فهرست کارکنان، ردیف‌های
              پایینِ جدول از زیرِ همان لکه رد می‌شدند و کم‌رنگ‌تر از
              بقیه دیده می‌شدند.

              ⚠️ برگرداندنش با `-z-10` (پشتِ محتوا) هم جواب نمی‌دهد:
              دقیقاً همان چیزی است که پیش‌تر امتحان شده و چون ظرف و
              کارت‌ها هر دو `--pf-surface` هستند، تصویر کاملاً پنهان
              می‌شد. یعنی این نشان یا روی محتواست یا اصلاً دیده نمی‌شود؛
              حالت سومی ندارد.
            */}

            {/* هر ماژول در Boundary مستقل رندر می‌شود — اصل P-06.
                resetKey باعث می‌شود با تغییر مسیر، خطای قبلی پاک شود. */}
            <ErrorBoundary moduleName={currentModule?.title} resetKey={location.pathname}>
              <Suspense fallback={<LoadingState label="در حال بارگذاری بخش…" />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>

            {/* گفتگو بیرونِ Outlet است تا با عوض‌شدنِ صفحه از بین نرود و
                Rocket.Chat هر بار از نو بارگذاری نشود — توضیح در خودش. */}
            <ChatFrameHost />
          </div>
        </main>
      </div>

      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* دعوت به فعال‌سازی اعلان — خودش تصمیم می‌گیرد که دیده شود یا نه.

          ⚠️ داخل Shell است و نه صفحهٔ اعلان‌ها: کسی که خبر ندارد اعلانی
          هست، سراغ صفحهٔ اعلان‌ها هم نمی‌رود. */}
      <PushEnrollmentGate />
    </div>
  )
}
